'use client';
import React, { useState, useEffect } from 'react';
import Logo from './assets/logo.svg';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from './store/authStore.js';
import SSOKeyImage from './assets/images/sso-key.svg';
import SSOCloudImage from './assets/images/sso-cloud.svg';
import { useClubStore } from './store/clubStore';

import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  TransitionChild,
} from '@headlessui/react';
import {
  Bars3Icon,
  TrophyIcon,
  FlagIcon,
  DocumentChartBarIcon,
  XMarkIcon,
  UserGroupIcon,
  PhoneIcon,
} from '@heroicons/react/24/outline';
import SelectBoxClub from './components/customLayout/selectBoxClub';
import { useDialogStore } from './store/dialogStore';
import axiosInstance from './axiosConfig';
import { useGlobalStore } from './store/globalStore';

const navigation = [
  { name: 'Ligen', href: '/league', icon: TrophyIcon, current: true },
  { name: 'Turniere', href: '/tournaments', icon: FlagIcon, current: false },
  {
    name: 'Jahresmeldungen',
    href: '/annual-reports',
    icon: DocumentChartBarIcon,
    current: false,
  },
  {
    name: 'Daten ändern',
    href: '/profile',
    icon: UserGroupIcon,
    current: false,
  },
  {
    name: 'Unterstützung',
    href: '/support',
    icon: PhoneIcon,
    current: false,
  },
];

export default function applicationLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const myClub = useClubStore((state) => state.myClub);
  const clubs = useClubStore((state) => state.clubs);
  const setClubs = useClubStore((state) => state.setClubs);
  const setMyClub = useClubStore((state) => state.setMyClub);
  const setToken = useAuthStore((state) => state.setToken);
  const setClubLoginTime = useClubStore((state) => state.setClubLoginTime);
  const openDialog = useDialogStore((state) => state.openDialog);
  const closeDialog = useDialogStore((state) => state.closeDialog);
  const clearToken = useAuthStore((state) => state.logout);
  const userRole = useAuthStore((state) => state.role);
  const isLoading = useGlobalStore((state) => state.isLoading);

  const switch2club = async (club_id: number) => {
    const response = await axiosInstance.post('/switch/club', { club_id });
    const res = response.data;
    setToken(res.token);
    await getMyClubData();
    setClubLoginTime(club_id);
    navigate('/tournaments');
  };

  const getMyClubData = async () => {
    const response = await axiosInstance.get('/club');
    const res = response.data;
    setMyClub(res);
  };

  const getClubData = async () => {
    if (userRole !== 'admin') return;
    if (clubs.length !== 0) return;

    try {
      const response = await axiosInstance.get(`/club/list`);

      setClubs(response.data);
    } catch (error) {
      console.error('Fehler beim Abrufen der Club-Daten:', error);
    }
  };

  useEffect(() => {
    getClubData();
    getMyClubData();
  }, []);

  const updatedNavigation = navigation.map((item) => ({
    ...item,
    current: location.pathname.startsWith(item.href),
  }));

  const handleLogout = () => {
    openDialog(
      <div className="text-md mt-10">
        <svg
          className="mx-auto"
          width="60"
          height="60"
          viewBox="0 0 60 60"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M29.9896 7.5H7.5V52.5H30"
            stroke="black"
            stroke-width="5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M41.25 41.25L52.5 30L41.25 18.75"
            stroke="black"
            stroke-width="5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M20 29.9896H52.5"
            stroke="black"
            stroke-width="5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>

        <div className="px-5 my-4 text-sm">
          ⚠️ Wenn Sie fortfahren, wird Ihre aktuelle Sitzung beendet und Sie
          werden zur Anmeldeseite zurückgeleitet.
        </div>
      </div>,
      () => {
        clearToken();
        navigate('/');
        closeDialog();
      },
    );
  };

  return (
    <>
      <div className="text-sm">
        <Dialog
          open={sidebarOpen}
          onClose={setSidebarOpen}
          className="relative z-50 lg:hidden"
        >
          <DialogBackdrop
            transition
            className="fixed inset-0 bg-gray-900/80 transition-opacity duration-300 ease-linear data-[closed]:opacity-0"
          />

          <div className="fixed inset-0 flex">
            <DialogPanel
              transition
              className="relative mr-16 flex w-full flex-1 transform transition duration-300 ease-in-out data-[closed]:-translate-x-full"
            >
              <TransitionChild>
                <div className="absolute left-full top-0 flex w-16 justify-center pt-5 duration-300 ease-in-out data-[closed]:opacity-0">
                  <button
                    type="button"
                    onClick={() => setSidebarOpen(false)}
                    className="-m-2.5 p-2.5"
                  >
                    <span className="sr-only">Close sidebar</span>
                    <XMarkIcon
                      aria-hidden="true"
                      className="size-6 text-white"
                    />
                  </button>
                </div>
              </TransitionChild>
              {/* Sidebar component, swap this element with another sidebar if you like */}
              <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-white px-6 pb-4">
                <div className="flex h-16 shrink-0 items-center">
                  <img alt="" src={Logo} className="h-8 w-auto" />
                </div>
                <nav className="flex flex-1 flex-col">
                  <ul role="list" className="flex flex-1 flex-col gap-y-7">
                    <li>
                      <ul role="list" className="-mx-2 space-y-1">
                        {updatedNavigation.map((item) => (
                          <li key={item.name}>
                            <Link
                              to={item.href}
                              className={`group flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold ${item.current ? 'bg-gray-50 text-green-600' : 'text-gray-700 hover:bg-gray-50 hover:text-green-600'}`}
                            >
                              <item.icon
                                aria-hidden="true"
                                className={`size-6 shrink-0 ${item.current ? 'text-green-600' : 'text-gray-400 group-hover:text-green-600'}`}
                              />
                              {item.name}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </li>

                    <li className="mt-auto">
                      <button
                        onClick={handleLogout}
                        className="group -mx-2 flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold text-red-700 hover:bg-gray-50 hover:text-red-800"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth="1.5"
                          stroke="currentColor"
                          className="size-6"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M15.75 9V5.25A2.25 2.25 0 0 0 13.5 3h-6a2.25 2.25 0 0 0-2.25 2.25v13.5A2.25 2.25 0 0 0 7.5 21h6a2.25 2.25 0 0 0 2.25-2.25V15M12 9l-3 3m0 0 3 3m-3-3h12.75"
                          />
                        </svg>
                        Abmelden
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </DialogPanel>
          </div>
        </Dialog>

        {/* Static sidebar for desktop */}
        <div className="hidden lg:fixed lg:inset-y-0 lg:z-30 lg:flex lg:w-72 lg:flex-col">
          {/* Sidebar component, swap this element with another sidebar if you like */}
          <div className="flex  grow flex-col gap-y-5 overflow-y-auto border-r border-gray-200 bg-neutral-50 px-6 pb-4">
            <div className="flex h-10 mt-4 shrink-0 items-center relative overflow-hidden">
              <div className="w-10 h-20 -top-4 bg-gradient-to-r from-[#fff]/10 to-[#fff]/60 absolute z-10 animate-slide"></div>
              <img
                alt=""
                src={Logo}
                className="h-9 relative -top-2 animate__animated animate__fadeInLeft mt-4 w-auto"
              />
            </div>
            <nav className="flex flex-1 flex-col ">
              <ul
                role="list"
                className="flex animate__animated animate__fadeInLeft flex-1 flex-col gap-y-7"
              >
                <li>
                  <ul role="list" className="-mx-2 space-y-1">
                    {updatedNavigation.map((item) => (
                      <li key={item.name}>
                        <Link
                          to={item.href}
                          className={`group flex transition-all gap-x-3 rounded-md p-2 text-sm/6 font-semibold ${item.current ? 'bg-white shadow-sm border-l-2 border-[#52A445] text-[#52A445]' : 'text-gray-700 hover:bg-gray-50 hover:text-[#52A445]'}`}
                        >
                          <item.icon
                            aria-hidden="true"
                            className={`size-6 shrink-0 ${item.current ? 'text-[#52A445]' : 'text-gray-400 group-hover:text-[#52A445]'}`}
                          />
                          <div className="flex justify-between w-full items-center">
                            <span
                              className={`${item.current && 'font-bold text-md'}`}
                            >
                              {item.name}
                            </span>
                            {item.current && (
                              <div className="w-0 h-0 animate__animated animate__fadeInLeft border-t-4 border-t-transparent border-b-4 border-b-transparent border-l-4 border-l-red-500"></div>
                            )}
                          </div>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </li>

                <li className="mt-auto">
                  <button
                    onClick={handleLogout}
                    className="group border w-full border-red-100 hover:border-red-500 -mx-2 flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold text-red-700 hover:bg-gray-50 hover:text-red-800"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="1.5"
                      stroke="currentColor"
                      className="size-6"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M15.75 9V5.25A2.25 2.25 0 0 0 13.5 3h-6a2.25 2.25 0 0 0-2.25 2.25v13.5A2.25 2.25 0 0 0 7.5 21h6a2.25 2.25 0 0 0 2.25-2.25V15M12 9l-3 3m0 0 3 3m-3-3h12.75"
                      />
                    </svg>
                    Abmelden
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </div>

        <div className="lg:pl-72 bg-dotdot min-h-screen">
          <div className="sticky top-0 z-40">
            <div className="flex h-16 items-center gap-x-4 border-b border-gray-200 bg-white lg:bg-white/0 lg:backdrop-blur-2xl px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-0 lg:shadow-none">
              <button
                type="button"
                onClick={() => setSidebarOpen(true)}
                className="-m-2.5 p-2.5 text-gray-700 lg:hidden"
              >
                <span className="sr-only">Open sidebar</span>
                <Bars3Icon aria-hidden="true" className="size-6" />
              </button>

              {/* Separator */}
              <div
                aria-hidden="true"
                className="h-6 w-px fixed left-0 right-0 bg-gray-200 lg:hidden"
              />

              <div className="flex items-center flex-1 gap-x-4 self-stretch lg:gap-x-2">
                {myClub?.name && (
                  <div className="w-10 md:ml-4 hidden animate__animated animate__fadeIn lg:flex rounded-full text-white font-bold cursor-default items-center justify-center text-2xl h-10 bg-orange-600">
                    {myClub?.name?.charAt(0)}
                  </div>
                )}
                <div
                  key={myClub?.id}
                  className="grid px-2 flex-1 grid-cols-1 animate__animated animate__fadeIn "
                >
                  <div className="font-bold md:text-xl text-xs">
                    {myClub?.name}
                  </div>
                  <div className="text-gray-600">
                    {' '}
                    {myClub?.club_number} - {myClub?.sender_name}
                  </div>
                </div>
                {userRole === 'admin' && (
                  <div
                    key={clubs.filter((item) => item.loginTime).join('-')}
                    className="flex md:mr-4 mr-0 items-center gap-x-4 lg:gap-x-6"
                  >
                    <SelectBoxClub
                      label={`Zugang zu Golfclubs (${clubs.length})`}
                      items={clubs}
                      activeItems={[]}
                      multipleSelect={false}
                      onItemClick={(club) => {
                        openDialog(
                          <div className="text-md mt-10">
                            <div className="relative h-[60px]">
                              <img
                                src={SSOCloudImage}
                                alt="SSO cloud"
                                className="mx-auto absolute left-0 right-0"
                              />
                              <img
                                src={SSOKeyImage}
                                alt="SSO key"
                                className="mx-auto animate-pulse absolute left-0 right-0"
                              />
                            </div>
                            <div className="flex my-4 text-sm font-medium items-center justify-center gap-2">
                              <div className="text-gray-400">
                                Zugang zum Club:
                              </div>
                              <div>
                                {club.club_number} / {club.name}
                              </div>
                            </div>
                            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 text-xs">
                              ⚠️ Bitte beachten Sie, dass nach dem Login in den
                              Club die Verantwortung für Änderungen im Profil
                              und in der Turnier-App bei Ihnen liegt. Wir bitten
                              Sie, bei der Durchführung von Änderungen die
                              nötige Sorgfalt walten zu lassen.
                            </div>
                          </div>,
                          () => {
                            switch2club(club.id);
                            closeDialog();
                          },
                        );
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          <main className="py-10 mx-auto">
            <div className="mx-auto lg:mx-auto lg:max-w-7xl text-left">
              {isLoading && (
                <div className="fixed backdrop-blur-sm left-0 right-0 flex items-center top-0 h-2 bg-white/50 z-50">
                  <div className="h-2 animate-progress border-r-4 border-red-500 bg-[#52A445] w-[10%]"></div>
                </div>
              )}
              {children}
            </div>
          </main>
        </div>
      </div>
    </>
  );
}
